import '../../../styles/restScreens.css';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabaseClient } from '../../../config/supabase';
import { toneTextStyle } from '../clientsConstants';
import {
    accessOn, fetchColleagues, fetchIncomingShares, fetchMyContacts, fetchSharedWithMe, filterPersonal,
} from '../../../lib/personalContacts';
import { buildPersonalExportCsv } from '../../../lib/personalContactCsv';
import { downloadCsv } from '../../../lib/contactCsv';
import { buildVcard, downloadVcard } from '../../../lib/vcard';
import PersonalContactDisclosure, { PERSONAL_COLUMNS } from './PersonalContactDisclosure';
import PersonalImportPanel from './PersonalImportPanel';
import PersonalSharePanel from './PersonalSharePanel';

import { BTN } from '../../ui/platformStandard';
// BL-177 PR D8: the "My Contacts" and "Shared with me" tabs of the CRM. Same List / Card / Hybrid views and click-to-reveal grammar as
// company contacts. Everything here is loaded through row-level security: "My Contacts" is the signed-in person's own list (explicitly
// filtered to them), "Shared with me" is what colleagues chose to share (View or Can-edit), and nothing else can ever come back.
const btn = BTN;

export default function PersonalContactsPanel({ tab, userId, status, viewMode, search, entities = [], version = 0, onAdd, client = supabaseClient }) {
    const isMine = tab === 'mine';
    const [contacts, setContacts] = useState([]);
    const [incoming, setIncoming] = useState([]);
    const [colleagues, setColleagues] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showImport, setShowImport] = useState(false);
    const [showShareList, setShowShareList] = useState(false);

    const load = useCallback(async () => {
        setError(null);
        try {
            const [rows, people, inc] = await Promise.all([
                isMine ? fetchMyContacts(client, userId) : fetchSharedWithMe(client, userId),
                status.sharing_enabled ? fetchColleagues(client) : Promise.resolve([]),
                isMine ? Promise.resolve([]) : fetchIncomingShares(client, userId),
            ]);
            setContacts(rows); setColleagues(people); setIncoming(inc);
        } catch (err) { setError(err.message || String(err)); setContacts([]); }
        setLoading(false);
    }, [client, isMine, status.sharing_enabled, userId]);
    useEffect(() => { setLoading(true); load(); }, [load, version]);

    const filtered = useMemo(() => filterPersonal(contacts, search), [contacts, search]);
    const panel = 'bg-[var(--surface-raised)] border-[var(--surface-border)]';

    const rowFor = (contact, mode) => (
        <PersonalContactDisclosure key={contact.id} contact={contact} mode={mode} userId={userId} colleagues={colleagues}
            access={accessOn(contact, userId, incoming)} entities={entities} sharingEnabled={status.sharing_enabled} onChanged={load} />
    );

    return (
        <div data-m6-layout="clients" data-m6-component="PersonalContactsPanel" data-testid={isMine ? 'my-contacts-panel' : 'shared-contacts-panel'}>
            {isMine && (
                <div className="mb-3 flex flex-wrap items-center gap-2">
                    <button type="button" className={BTN} onClick={onAdd}>+ Add to My Contacts</button>
                    <button type="button" className={btn} aria-pressed={showImport} onClick={() => setShowImport(v => !v)}>Import</button>
                    <button type="button" className={btn} disabled={!contacts.length}
                        onClick={() => downloadCsv(`my-contacts-${new Date().toISOString().slice(0, 10)}.csv`, buildPersonalExportCsv(contacts))}>Export CSV</button>
                    <button type="button" className={btn} disabled={!contacts.length}
                        onClick={() => downloadVcard(`my-contacts-${new Date().toISOString().slice(0, 10)}.vcf`, buildVcard(contacts))}>Export vCard</button>
                    {status.sharing_enabled && (
                        <button type="button" className={btn} aria-pressed={showShareList} onClick={() => setShowShareList(v => !v)}>Share my whole list…</button>
                    )}
                    <span className="text-xs text-[var(--ink-muted)]">Private to you. Import and export cover your own list only.</span>
                </div>
            )}
            {!isMine && (
                <p className="mb-3 text-sm text-[var(--ink-muted)]">Contacts colleagues chose to share with you. Their names also show on your own conversations. You can edit one only if it was shared as “Can edit”; you can add any of them to Company contacts.</p>
            )}
            {isMine && showImport && <PersonalImportPanel onClose={() => setShowImport(false)} onDone={load} client={client} />}
            {isMine && showShareList && status.sharing_enabled && <div className="mb-4"><PersonalSharePanel userId={userId} contact={null} client={client} /></div>}

            {error && <div role="alert" className="mb-4 rounded-lg border p-3 text-sm" style={toneTextStyle('alert')}>Couldn’t load contacts: {error}</div>}

            {loading ? (
                <div className="p-8 text-center text-[var(--ink-muted)]">Loading…</div>
            ) : filtered.length === 0 ? (
                <div className={`rounded-lg border p-10 text-center ${panel}`}>
                    <div className="font-medium text-[var(--ink)]">
                        {contacts.length ? 'Nothing matches that search' : (isMine ? 'No contacts in your list yet' : 'Nothing has been shared with you')}
                    </div>
                    <div className="mt-1 text-sm text-[var(--ink-muted)]">
                        {contacts.length ? 'Try a different search.'
                            : (isMine ? 'Add one, or import a CSV or vCard file. Only you can see your list unless you share it.' : 'When a colleague shares a contact or their whole list, it shows up here.')}
                    </div>
                </div>
            ) : viewMode === 'List' ? (
                <div className={`overflow-hidden rounded-lg border shadow-sm ${panel}`}>
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-[var(--surface-sunken)]">
                                <tr>{PERSONAL_COLUMNS.map(c => <th key={c} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-[var(--ink-muted)]">{c}</th>)}</tr>
                            </thead>
                            <tbody className="divide-y divide-[var(--surface-border)]">{filtered.map(contact => rowFor(contact, 'List'))}</tbody>
                        </table>
                    </div>
                </div>
            ) : viewMode === 'Hybrid' ? (
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2" data-testid="hybrid-grid">{filtered.map(contact => rowFor(contact, 'Hybrid'))}</div>
            ) : (
                <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">{filtered.map(contact => rowFor(contact, 'Card'))}</div>
            )}
        </div>
    );
}
